import { Grid, styled, TextField, Typography } from "@mui/material";
import { api } from "api";
import { AssignmentModelList } from "components/AssignmentModelList/AssignmentModelList";
import { InfoSnackBar } from "components/InfoSnackBar";
import { StyledButton } from "components/StyledButton";
import { SummaryCard } from "components/SummaryCard";
import { Assignment, getStatus, Status } from "helpers/assignment";
import { useAuthStatus } from "helpers/useAuthStatus";
import { ACTIVITIES_FETCH_ERR } from "messages";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { routes } from "routes";
import SearchIcon from "@mui/icons-material/Search";

const PREFIX = "AssignmentsDashboardPage";

const classes = {
	button: `${PREFIX}-button`,
	title: `${PREFIX}-title`,
};

const Root = styled("div")(() => ({
	[`& .${classes.button}`]: {
		marginTop: 15,
		marginRight: 5,
		float: "right",
	},

	[`& .${classes.title}`]: {
		marginLeft: 5,
		marginTop: 5,
		marginBottom: 15,
		float: "left",
	},
}));

export const AssignmentsDashboardPage = (): JSX.Element => {
	const { role } = useAuthStatus();
	const [assignments, setAssignments] = useState<Assignment[]>([]);
	const [allAssignments, setAllAssignments] = useState<Assignment[]>([]);
	const [assignmentName, setAssignmentName] = useState("");
	const [err, setErr] = useState(false);

	const componentMounted = useRef(true);

	const stateCount = (state: Status): number => {
		let count = 0;
		assignments.forEach((el) => {
			if (el.status === state) {
				count += 1;
			}
		});
		return count;
	};

	useEffect(() => {
		api
			.getAssignments()
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					const now = new Date();
					setAssignments(
						(res.data || []).map((ass) => ({
							...ass,
							status: getStatus(ass, now),
						})),
					);
					setAllAssignments(
						(res.data || []).map((ass) => ({
							...ass,
							status: getStatus(ass, now),
						})),
					);
				}
			})
			.catch(() => {
				setErr(true);
			});
		return () => {
			componentMounted.current = false;
		};
	}, []);

	return (
		<Root>
			<Grid container>
				<Grid item xs={role === "student" ? 10 : 8}>
					<Grid
						container
						justifyContent="flex-start"
						style={{ flexGrow: "inherit" }}
					>
						<SummaryCard count={stateCount("In Progress")}>
							ON-GOING ASSIGNMENTS
						</SummaryCard>
						<SummaryCard count={stateCount("Scheduled")}>
							SCHEDULED ASSIGNMENTS
						</SummaryCard>
						<SummaryCard count={stateCount("Completed")}>
							COMPLETED ASSIGNMENTS
						</SummaryCard>
					</Grid>
				</Grid>
				<Grid item xs={2}>
					<TextField
						id="assignment-name"
						label="Assignment Name"
						variant="outlined"
						value={assignmentName}
						onChange={(e) => {
							const searchName = e.target.value;
							if (searchName.length < assignmentName.length) {
								// monitor backspace
								setAssignments(allAssignments);
							} else {
								let searchAssignments: Array<Assignment> = [];
								const regex = /^ *$/;
								// a name is null or empty string or all whitespace
								if (regex.exec(searchName) !== null) {
									setAssignments(allAssignments);
								} else {
									assignments.forEach((asgmt) => {
										if (
											asgmt.name
												.toLowerCase()
												.includes(searchName.trim().toLowerCase())
										) {
											searchAssignments = [...searchAssignments, asgmt];
										}
									});
									setAssignments(searchAssignments);
								}
							}
							setAssignmentName(searchName);
						}}
						fullWidth
						InputProps={{
							endAdornment: <SearchIcon />,
						}}
					/>
				</Grid>
				{role !== "student" && (
					<Grid item xs={2}>
						<StyledButton
							variant="contained"
							color="primary"
							className={classes.button}
							component={Link}
							to={routes._.assignments._.create.path}
							data-testid="create-button"
						>
							CREATE
						</StyledButton>
					</Grid>
				)}

				<Grid item xs={12}>
					{assignments.length === 0 ? (
						<Typography variant="h5" className={classes.title}>
							No ongoing assignments
						</Typography>
					) : (
						<AssignmentModelList assignments={assignments} show_csv={true} />
					)}
				</Grid>
			</Grid>

			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				message={ACTIVITIES_FETCH_ERR}
				severity="error"
			/>
		</Root>
	);
};
